// The pure, tested surface for the web edit-profile screen. The age-floor and
// username constants below are duplicated from services/profile-api/src/lib/
// on purpose: there is no shared client-logic package, the same reason
// apps/app/src/features/sessions/sessionsScreenLogic.ts duplicates its
// mobile twin. The age check uses UTC components only, matching the server,
// so the two never disagree at a day boundary.

import { SIGN_IN_METHODS_HEADING } from "../signInMethods/signInMethodsScreenLogic";

export const MINIMUM_AGE_YEARS = 13;
export const AGE_FLOOR_ANCHOR_OFFSET_MS = 12 * 60 * 60 * 1000;
export const BIRTHDAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const NAME_MAX_LENGTH = 100;
export const PHONE_MAX_LENGTH = 32;
export const USERNAME_PATTERN = /^[a-z0-9_]{3,20}$/;
export const USERNAME_CHANGE_COOLDOWN_DAYS = 30;

export const EDIT_PROFILE_HEADING = "Edit profile";
export const DISPLAY_NAME_LABEL = "Display name";
export const DISPLAY_NAME_NOTE =
  "This is the name your pool mates see. It comes from your first and last name.";
export const USERNAME_LABEL = "Username";
export const USERNAME_HINT = "You can change this once every 30 days.";
export const USERNAME_TAKEN_ERROR = "That username is taken. Try another.";
export const USERNAME_FORMAT_ERROR =
  "Usernames are 3 to 20 characters, using letters, numbers, and underscores.";
export const USERNAME_AVAILABLE_NOTE = "That username is available.";
export const FIRST_NAME_LABEL = "First name";
export const FIRST_NAME_REQUIRED_ERROR = "Enter your first name.";
export const LAST_NAME_LABEL = "Last name (optional)";
export const NAME_TOO_LONG_ERROR = "Keep this to 100 characters or fewer.";
export const BIRTHDAY_LABEL = "Birthday";
export const BIRTHDAY_HINT = "Only you can see this.";
export const BIRTHDAY_FORMAT_ERROR = "Enter a date as YYYY-MM-DD.";
export const BIRTHDAY_REQUIRED_ERROR = "Enter your birthday.";
export const UNDER_AGE_ERROR = "You have to be at least 13 to use Sportsmet.";
export const PHONE_LABEL = "Phone number";
export const PHONE_HINT =
  "Only you can see this. We never show it to other players.";
export const PHONE_TOO_LONG_ERROR = "Keep this to 32 characters or fewer.";
export const EMAIL_LABEL = "Email";
export const EMAIL_ABSENT_VALUE = "No email";
export const SAVE_LABEL = "Save";
export const SAVE_PENDING_LABEL = "Saving your profile";
export const CANCEL_LABEL = "Cancel";
export const DISCARD_CONFIRM_TITLE = "Discard your changes?";
export const DISCARD_CONFIRM_BODY =
  "Your edits to this profile won't be saved.";
export const DISCARD_CONFIRM_LABEL = "Discard changes";
export const LOAD_FAILURE_HEADLINE = "We could not load your profile";
export const LOAD_FAILURE_BODY = "Reload this page in a moment.";
export const NO_PROFILE_HEADLINE = "Your profile is not ready yet";
export const NO_PROFILE_BODY =
  "Finish setting up your account, then come back here.";
export const EDIT_PROFILE_ROUTE = "/account/edit";
export const ACCOUNT_ROUTE = "/account" as string;
export const SIGN_IN_METHODS_ROUTE = "/settings/sign-in-methods";
export const EMAIL_PRESENT_NOTE = `Change it in ${SIGN_IN_METHODS_HEADING}.`;
export const EMAIL_ABSENT_NOTE = `Add one in ${SIGN_IN_METHODS_HEADING}.`;
export const PHOTO_ADD_LABEL = "Add a photo";
export const PHOTO_CHANGE_LABEL = "Change photo";
export const PHOTO_UPLOADING_LABEL = "Uploading your photo";
export const PHOTO_UPLOAD_FAILED_MESSAGE =
  "That photo didn't upload. Try another one.";
export const PHOTO_PENDING_WITH_INITIALS_MESSAGE =
  "We're still checking that photo. Your initials stay up until it's ready.";
export const PHOTO_PENDING_WITH_PHOTO_MESSAGE =
  "We're still checking that photo. Your current one stays up until it's ready.";

export type typeEditProfileForm = {
  username: string;
  firstName: string;
  lastName: string;
  birthday: string; // "YYYY-MM-DD"
  phone: string;
};

export type typeEditProfileErrors = Partial<
  Record<keyof typeEditProfileForm, string>
>;

export function toFormState(profile: {
  username: string;
  firstName?: string | null;
  lastName?: string | null;
  birthday?: string | null;
  phone?: string | null;
}): typeEditProfileForm {
  return {
    username: profile.username,
    firstName: profile.firstName ?? "",
    lastName: profile.lastName ?? "",
    birthday: profile.birthday ?? "",
    phone: profile.phone ?? "",
  };
}

export function isAtLeastMinimumAge(input: {
  birthday: string;
  now: Date;
}): boolean {
  const anchor = new Date(input.now.getTime() - AGE_FLOOR_ANCHOR_OFFSET_MS);
  const birthDate = new Date(`${input.birthday}T00:00:00.000Z`);
  const anchorYear = anchor.getUTCFullYear();
  const anchorMonth = anchor.getUTCMonth();
  const anchorDay = anchor.getUTCDate();
  const birthYear = birthDate.getUTCFullYear();
  const birthMonth = birthDate.getUTCMonth();
  const birthDay = birthDate.getUTCDate();
  let age = anchorYear - birthYear;
  if (
    anchorMonth < birthMonth ||
    (anchorMonth === birthMonth && anchorDay < birthDay)
  ) {
    age -= 1;
  }
  return age >= MINIMUM_AGE_YEARS;
}

export function validateEditProfile(input: {
  form: typeEditProfileForm;
  now: Date;
}): typeEditProfileErrors {
  const errors: typeEditProfileErrors = {};
  const { form, now } = input;

  if (form.firstName.trim() === "") {
    errors.firstName = FIRST_NAME_REQUIRED_ERROR;
  } else if (form.firstName.trim().length > NAME_MAX_LENGTH) {
    errors.firstName = NAME_TOO_LONG_ERROR;
  }

  if (form.lastName.trim().length > NAME_MAX_LENGTH) {
    errors.lastName = NAME_TOO_LONG_ERROR;
  }

  if (form.birthday.trim() === "") {
    errors.birthday = BIRTHDAY_REQUIRED_ERROR;
  } else if (
    !BIRTHDAY_PATTERN.test(form.birthday) ||
    new Date(`${form.birthday}T00:00:00.000Z`).toISOString().slice(0, 10) !==
      form.birthday
  ) {
    errors.birthday = BIRTHDAY_FORMAT_ERROR;
  } else if (!isAtLeastMinimumAge({ birthday: form.birthday, now })) {
    errors.birthday = UNDER_AGE_ERROR;
  }

  if (form.phone.trim().length > PHONE_MAX_LENGTH) {
    errors.phone = PHONE_TOO_LONG_ERROR;
  }

  if (!USERNAME_PATTERN.test(normalizeUsername(form.username))) {
    errors.username = USERNAME_FORMAT_ERROR;
  }

  return errors;
}

export function buildProfileUpdateBody(input: {
  form: typeEditProfileForm;
  original: typeEditProfileForm;
}): {
  firstName?: string | null;
  lastName?: string | null;
  birthday?: string | null;
  phone?: string | null;
} {
  const { form, original } = input;
  const body: {
    firstName?: string | null;
    lastName?: string | null;
    birthday?: string | null;
    phone?: string | null;
  } = {};

  const firstName = form.firstName.trim();
  if (firstName !== original.firstName.trim()) {
    body.firstName = firstName === "" ? null : firstName;
  }

  const lastName = form.lastName.trim();
  if (lastName !== original.lastName.trim()) {
    body.lastName = lastName === "" ? null : lastName;
  }

  const birthday = form.birthday.trim();
  if (birthday !== original.birthday.trim()) {
    body.birthday = birthday === "" ? null : birthday;
  }

  const phone = form.phone.trim();
  if (phone !== original.phone.trim()) {
    body.phone = phone === "" ? null : phone;
  }

  return body;
}

export function hasUnsavedChanges(input: {
  form: typeEditProfileForm;
  original: typeEditProfileForm;
}): boolean {
  const { form, original } = input;
  return (
    form.username.trim() !== original.username.trim() ||
    form.firstName.trim() !== original.firstName.trim() ||
    form.lastName.trim() !== original.lastName.trim() ||
    form.birthday.trim() !== original.birthday.trim() ||
    form.phone.trim() !== original.phone.trim()
  );
}

export function isUsernameChanged(input: {
  form: typeEditProfileForm;
  original: typeEditProfileForm;
}): boolean {
  return (
    normalizeUsername(input.form.username) !==
    normalizeUsername(input.original.username)
  );
}

export function formatChangeWindowDate(isoDate: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(isoDate));
}

export function usernameWindowHint(isoDate: string): string {
  return `You can change your username again on ${formatChangeWindowDate(isoDate)}.`;
}

export function normalizeUsername(value: string): string {
  return value.trim().toLowerCase();
}
